import { CartUpdateEvent } from '@theme/events';

/**
 * @typedef {{ item_count: number }} Cart
 * @typedef {Record<string, string>} CartSections
 * @typedef {[string, string | null]} SectionEntry
 */

const EASYGIFT_INTERNAL_MARKER = '_easygift_internal=true';
const EASYGIFT_MODAL_SELECTOR = '#aca-modal-container';
const EASYGIFT_CLOSE_SELECTOR = '#aca-close-icon-container';
const EASYGIFT_ROOT_SELECTOR = '#aca-modal';
const EASYGIFT_KEYBOARD_FOCUS_CLASS = 'maurten-easygift-keyboard-focus';
const EASYGIFT_INITIAL_FOCUS_SELECTOR = [
  EASYGIFT_CLOSE_SELECTOR,
  'select',
  '.aca-product-dropdown',
  '.aca-product-variants',
  '.aca-variant-subscriptions',
].join(',');
const SYNC_DELAY_MS = 150;

/** @type {number | undefined} */
let syncTimeout;
const easyGiftXhrRequests = new WeakSet();
const normalizedEasyGiftDialogs = new WeakSet();

/** @returns {HTMLElement | null} */
function getEasyGiftModalRoot() {
  const modalRoot = document.querySelector(EASYGIFT_ROOT_SELECTOR);

  return modalRoot instanceof HTMLElement ? modalRoot : null;
}

function enableEasyGiftKeyboardFocus() {
  getEasyGiftModalRoot()?.classList.add(EASYGIFT_KEYBOARD_FOCUS_CLASS);
}

function disableEasyGiftKeyboardFocus() {
  getEasyGiftModalRoot()?.classList.remove(EASYGIFT_KEYBOARD_FOCUS_CLASS);
}

/**
 * @param {KeyboardEvent} event
 */
function handleEasyGiftKeydown(event) {
  if (event.key === 'Tab') {
    enableEasyGiftKeyboardFocus();
  }
}

/**
 * @param {HTMLDialogElement} dialog
 */
function clearEasyGiftInitialFocus(dialog) {
  disableEasyGiftKeyboardFocus();

  const closeButton = dialog.querySelector(EASYGIFT_CLOSE_SELECTOR);

  if (closeButton instanceof HTMLElement) {
    closeButton.classList.remove('aca-force-focus');
  }

  if (
    document.activeElement instanceof HTMLElement &&
    dialog.contains(document.activeElement) &&
    document.activeElement.matches(EASYGIFT_INITIAL_FOCUS_SELECTOR)
  ) {
    document.activeElement.blur();
  }
}

function normalizeEasyGiftInitialFocus() {
  const dialog = document.querySelector(EASYGIFT_MODAL_SELECTOR);

  if (!(dialog instanceof HTMLDialogElement)) return;

  if (!dialog.open) {
    normalizedEasyGiftDialogs.delete(dialog);
    return;
  }

  if (normalizedEasyGiftDialogs.has(dialog)) return;

  normalizedEasyGiftDialogs.add(dialog);

  [0, 50, 150].forEach((delay) => {
    window.setTimeout(() => clearEasyGiftInitialFocus(dialog), delay);
  });
}

document.addEventListener('keydown', handleEasyGiftKeydown, true);
document.addEventListener('pointerdown', disableEasyGiftKeyboardFocus, true);

const easyGiftModalObserver = new MutationObserver(normalizeEasyGiftInitialFocus);

easyGiftModalObserver.observe(document.documentElement, {
  attributeFilter: ['class', 'open'],
  attributes: true,
  childList: true,
  subtree: true,
});

normalizeEasyGiftInitialFocus();

function requestCartSync() {
  window.clearTimeout(syncTimeout);
  syncTimeout = window.setTimeout(syncCart, SYNC_DELAY_MS);
}

async function syncCart() {
  try {
    const [cart, sections] = await Promise.all([fetchCart(), fetchCartSections()]);

    if (!cart) return;

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'maurten-easygift-cart-sync', {
        itemCount: cart.item_count,
        sections,
        source: 'maurten-easygift-cart-sync',
      })
    );
  } catch (error) {
    console.error('Failed to sync EasyGift cart update:', error);
  }
}

/** @returns {Promise<Cart | null>} */
async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) return null;

  return response.json();
}

/** @returns {Promise<CartSections>} */
async function fetchCartSections() {
  const sectionIds = getCartSectionIds();

  if (!sectionIds.length) return {};

  const sectionEntries = /** @type {SectionEntry[]} */ (
    await Promise.all(
      sectionIds.map(async (sectionId) => {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId.replace(/^shopify-section-/, ''));
        url.searchParams.sort();

        const response = await fetch(url.toString(), {
          headers: {
            Accept: 'text/html',
          },
        });

        if (!response.ok) return [sectionId, null];

        return [sectionId, await response.text()];
      })
    )
  );

  return Object.fromEntries(sectionEntries.filter(isRenderedSectionEntry));
}

/** @returns {string[]} */
function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => /** @type {HTMLElement} */ (component).dataset.sectionId)
    .filter(isString);
}

/**
 * @param {string | undefined} value
 * @returns {value is string}
 */
function isString(value) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * @param {SectionEntry} entry
 * @returns {entry is [string, string]}
 */
function isRenderedSectionEntry(entry) {
  return Boolean(entry[1]);
}

/**
 * @param {RequestInfo | URL} input
 * @returns {boolean}
 */
function isEasyGiftInternalCartRequest(input) {
  const url = input instanceof Request ? input.url : input.toString();

  return typeof url === 'string' && url.includes(EASYGIFT_INTERNAL_MARKER);
}

const originalFetch = window.fetch;

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
window.fetch = async function maurtenEasyGiftFetchSync(input, init) {
  const isEasyGiftRequest = isEasyGiftInternalCartRequest(input);
  const response = await originalFetch(input, init);

  if (isEasyGiftRequest && response.ok) {
    requestCartSync();
  }

  return response;
};

const originalOpen = XMLHttpRequest.prototype.open;
const originalSend = XMLHttpRequest.prototype.send;

/**
 * @param {string} method
 * @param {string | URL} url
 * @param {boolean} [async]
 * @param {string | null} [username]
 * @param {string | null} [password]
 * @this {XMLHttpRequest}
 */
XMLHttpRequest.prototype.open = function maurtenEasyGiftOpen(method, url, async, username, password) {
  if (isEasyGiftInternalCartRequest(url)) {
    easyGiftXhrRequests.add(this);
  }

  return originalOpen.call(this, method, url, async ?? true, username, password);
};

/**
 * @param {Document | XMLHttpRequestBodyInit | null} [body]
 * @this {XMLHttpRequest}
 */
XMLHttpRequest.prototype.send = function maurtenEasyGiftSend(body) {
  if (easyGiftXhrRequests.has(this)) {
    this.addEventListener('loadend', () => {
      if (this.status >= 200 && this.status < 300) {
        requestCartSync();
      }

      easyGiftXhrRequests.delete(this);
    });
  }

  return originalSend.call(this, body);
};
